import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi, useLoaderData } from '@tanstack/react-router';

import {
  getMediaOptions,
  type ImagesType,
  type ProjectImageType,
  useMedia,
  useProjectMedia,
} from '@/api/media';
import { useEstate } from '@/features/estates-property/api/estate';
import { canWrite } from '@/features/permissions/resource-permissions';

import { useDeleteImage } from '../api/delete-image';
import { useUploadImage } from '../api/upload-image';
import { ACCEPTED_IMAGE_INPUT_TYPE, uploadImagesSchema } from '../schema/upload-media';
import { MediaData } from './media-data';
import { UploadMediaModal, type UploadMediaModalType } from './upload-media-modal';
import { useModal } from '@/components/modals/new/use-modal';
import { ConfirmationModal } from '@/components/modals/confirmation-modal';
import { SelectThumbnailModal } from '@/features/estates-property/components/select-thumbnail-modal';

const route = getRouteApi(
  '/_authenticated/_sidebar-layout/estates/$estateId/_dashboard-layout/media/images'
);

export const MediaImagesDashboard = () => {
  const { estateId } = route.useParams();

  const { t } = useTranslation();
  const thumbnailModal = useModal();
  const uploadModal = useModal();
  const deleteModal = useModal();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const queryClient = useQueryClient();
  const { data } = useMedia({ id: estateId });
  const { data: estate } = useEstate({ id: estateId });
  const { data: projectMedia } = useProjectMedia({ id: estate?.projectId, forEstate: true });
  const estateDashboard = useLoaderData({
    from: '/_authenticated/_sidebar-layout/estates/$estateId/_dashboard-layout',
  });

  const canWriteMedia = canWrite(estateDashboard.metadata?.permissions, 'media');

  const estateImages = useMemo(() => Object.values(data.images).flat(), [data.images]);

  const dataImages = useMemo(() => {
    const dates = new Set<string>([
      ...Object.keys(data.images),
      ...Object.keys(projectMedia.images),
    ]);
    const groupedImages = Array.from(dates).reduce<
      Record<string, ImagesType[string] | ProjectImageType[]>
    >((acc, date) => {
      const estateImages = data.images[date] || [];
      const projectImages = projectMedia.images[date] || [];
      acc[date] = [...estateImages, ...projectImages];
      return acc;
    }, {});
    return groupedImages;
  }, [data, projectMedia]);

  const { mutate: deleteImage } = useDeleteImage({
    mutationConfig: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getMediaOptions(estateId).queryKey });
        toast.success(t('successfullyDeletedImage'));
      },
    },
  });

  const { mutate: uploadImage } = useUploadImage({
    mutationConfig: {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: getMediaOptions(estateId).queryKey }),
    },
  });

  const handleDelete = (ids: string[]) => {
    setSelectedIds(ids);
    deleteModal.open();
  };

  const submitHandler = async (data: UploadMediaModalType) => {
    // Convert File[] to FileList
    const dataTransfer = new DataTransfer();
    data.files.forEach((file) => dataTransfer.items.add(file));
    await uploadImage({ images: dataTransfer.files, estateId });
    uploadModal.dismiss();
  };

  const handleOpenModal = () => {
    uploadModal.open();
  };

  return (
    <>
      <MediaData
        data={dataImages}
        canWriteMedia={canWriteMedia}
        onUpload={handleOpenModal}
        handleDelete={handleDelete}
        onChooseThumbnail={thumbnailModal.open}
        isThumbnailDisabled={!estateImages.length}
        page="images"
        type="estate"
        estateId={estateId}
      />
      <SelectThumbnailModal
        modal={thumbnailModal}
        id={estateId}
        images={estateImages}
        type="estate"
      />
      <UploadMediaModal
        modal={uploadModal}
        submitHandler={submitHandler}
        acceptedType={ACCEPTED_IMAGE_INPUT_TYPE}
        acceptedMediaSchema={uploadImagesSchema()}
      />
      <ConfirmationModal
        modal={deleteModal}
        title={`${t('delete')} ${t('image')} `}
        description={t('confirmDeleteDocument')}
        confirmationText={t('delete')}
        destructive
        onSubmit={async () => await deleteImage({ estateId, imageIds: selectedIds })}
      />
    </>
  );
};
